/**
 * `wayai files pull` and `wayai files push`: a file type, or one folder of it, as a local folder
 * of files — knowledge kept as code, reviewed as a diff before it reaches a base.
 *
 * These are DATA commands. They name their base and write to whichever base that is, production
 * included; nothing here is config, so there is no preview step (`wayai push bases/<base>` is the
 * config verb, and it stays preview-only). Every request goes through WayAI's `/v1` file routes,
 * so each version a push writes names the person or agent who pushed it.
 *
 * A push does not overwrite an edit it did not see, with one gap below. An update or a prune
 * carries `If-Match` with the content hash the last pull recorded, so the base refuses it if the
 * file changed since — whether the plan saw the change or it landed in between — and a path the
 * base changed, deleted or gained
 * since the pull is reported as a conflict, not written. The one gap is a create: the base's file
 * routes take no create-only check, so a new file is checked against the listing the push read,
 * and a file created at that path after the read is replaced (it stays in the file's history).
 */

import { getEndpointConfig } from '@wayai/core';
import { DATA_PROXY_PREFIX } from '@wayai/core/contracts';
import { ApiError } from '../../lib/api-client.js';
import { expected } from '../../lib/expected.js';
import { computeHash, guessMimeType } from '../../lib/resource-files.js';
import { sanitizeTerminalText } from '../../lib/terminal-output.js';
import { confirm } from '../../lib/utils.js';
import { lineDiffOf, printSection } from '../../communities/listing.js';
import { Pacer } from '../../communities/pacer.js';
import { createDataClient, type DataClient } from '../client.js';
import { isInteractive, pathSegment, pathSegments } from '../helpers.js';
import { fileWriteHeaders, labelValue } from './headers.js';
import {
  OUTSIDE_SYNC,
  RECORD_FILE,
  assertPushable,
  assertRepresentable,
  defaultDir,
  describeSync,
  diskClash,
  matchesScan,
  ensureSyncDir,
  isOutsideSync,
  normalizeFolder,
  readLocal,
  readRecord,
  remotePath,
  removeLocal,
  scanLocal,
  syncDirExists,
  syncedFile,
  writeLocal,
  writeRecord,
  type SyncRecord,
} from './local.js';
import {
  planPull,
  planPush,
  type Clash,
  type Conflict,
  type PullConflict,
  type PullKept,
  type PushConflict,
  type PushPlan,
  type PushStep,
  type RemoteFile,
} from './plan.js';

/** Rows per listing request: the most the file listing returns. */
const LIST_PAGE = 1000;
/** Listing pages one run reads before refusing: a million files, or a server answering in circles. */
const MAX_LIST_PAGES = 1000;
/** A text file larger than this is listed as changed, not diffed line by line. */
const MAX_DIFF_BYTES = 256 * 1024;

/**
 * Requests a sync starts in any minute: four fifths of the per-person budget every data command
 * and the web console share (the `/api/data/*` limit in `packages/core/src/rate-limits.ts`), since
 * a sync spends one request per file. Read from that config, so the two cannot drift apart.
 */
export function filesSyncRequestsPerMinute(): number {
  const budget = getEndpointConfig(`${DATA_PROXY_PREFIX}/files`)?.userLimit;
  return budget ? Math.max(1, Math.floor((budget * 4) / 5)) : 60;
}

type Target = Pick<SyncRecord, 'base' | 'file_type' | 'folder'>;

/** The data-client calls a sync makes, each paced and retried past a 429. */
type Remote = Pick<DataClient, 'request' | 'upload' | 'download'>;

interface FileRow {
  path: string;
  version: number;
  content_sha256: string;
  content_type?: string;
  size?: number;
}

const MARK: Record<PushStep['op'], string> = { create: '+', update: '~', delete: '-' };

const safe = (rel: string) => sanitizeTerminalText(rel);

async function openRemote(): Promise<Remote> {
  const client = await createDataClient();
  const perMinute = filesSyncRequestsPerMinute();
  const pacer = new Pacer({
    perMinute,
    onWait: (ms, reason) =>
      console.error(
        reason === 'paced'
          ? `  Pacing at ${perMinute} requests a minute: waiting ${Math.ceil(ms / 1000)} s.`
          : `  The data request budget is spent (your other commands and the web console share it): waiting ${Math.ceil(ms / 1000)} s.`,
      ),
  });
  return {
    request: (method, path, body, headers) => pacer.run(() => client.request(method, path, body, headers)),
    upload: (path, bytes, contentType, headers) => pacer.run(() => client.upload(path, bytes, contentType, headers)),
    download: (path) => pacer.run(() => client.download(path)),
  };
}

function filesRoot(t: Target): string {
  return `/v1/${pathSegment(t.base, '--base')}/files/${pathSegment(t.file_type, 'file_type')}`;
}

function filePath(t: Target, rel: string): string {
  return `${filesRoot(t)}/${pathSegments(remotePath(t.folder, rel))}`;
}

/** The command that pulls this sync, for messages that send the user to it. */
function pullCommand(t: Target, dir: string | undefined, overwrite = false): string {
  const folder = t.folder ? ` --folder ${t.folder}` : '';
  const dirFlag = dir === undefined ? '' : ` --dir ${dir}`;
  return sanitizeTerminalText(`wayai files pull ${t.file_type} --base ${t.base}${folder}${dirFlag}${overwrite ? ' --overwrite' : ''}`);
}

interface SyncOptions {
  base: string;
  fileType: string;
  folder?: string;
  dir?: string;
}

/**
 * The sync an invocation names, its local folder, and the record already there — refused when the
 * record names another sync, since its versions are that sync's and could check no other's writes.
 */
function resolveSync(opts: SyncOptions): { target: Target; dir: string; existing: SyncRecord | null } {
  pathSegment(opts.base, '--base');
  pathSegment(opts.fileType, 'file_type');
  const target: Target = { base: opts.base, file_type: opts.fileType, folder: normalizeFolder(opts.folder) };
  const dir = opts.dir ?? defaultDir(target.file_type, target.folder);
  const existing = syncDirExists(dir) ? readRecord(dir) : null;
  if (
    existing &&
    (existing.base !== target.base || existing.file_type !== target.file_type || existing.folder !== target.folder)
  ) {
    throw expected(
      `${sanitizeTerminalText(dir)} was pulled from ${describeSync(existing)}, not ${describeSync(target)}. ` +
        "Its recorded versions are that base's, so they cannot check writes to another: name that sync, or use another --dir.",
    );
  }
  return { target, dir, existing };
}

/** Every live file of the file type whose path starts with `prefix` (`''`: all of them). */
async function listFiles(client: Remote, t: Target, prefix: string): Promise<FileRow[]> {
  const rows: FileRow[] = [];
  for (let page = 0; ; page++) {
    if (page === MAX_LIST_PAGES) {
      throw expected(`The listing of ${describeSync(t)} did not end after ${MAX_LIST_PAGES} pages. Sync a narrower --folder.`);
    }
    const qs = new URLSearchParams({ limit: String(LIST_PAGE), offset: String(page * LIST_PAGE) });
    if (prefix) qs.set('prefix', prefix);
    const pageRows = (await client.request<FileRow[]>('GET', `${filesRoot(t)}?${qs}`)) ?? [];
    for (const row of pageRows) if (row.path.startsWith(prefix)) rows.push(row);
    if (pageRows.length < LIST_PAGE) return rows;
  }
}

/**
 * The base's side of a sync: the client, after confirming the file type exists (a listing of one
 * that does not is simply empty), and every live file under the synced folder by path relative to
 * it. The folder is a path boundary (`hr/`), never a bare string prefix that would also match
 * `hrx/`. Names outside the sync are returned apart, so pull can say it left them alone.
 */
async function readBase(t: Target): Promise<{ client: Remote; remote: Map<string, RemoteFile>; excluded: string[] }> {
  const client = await openRemote();
  try {
    await client.request('GET', `/v1/${pathSegment(t.base, '--base')}/file-types/${pathSegment(t.file_type, 'file_type')}`);
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) {
      throw expected(sanitizeTerminalText(`Base "${t.base}" has no file type "${t.file_type}".`));
    }
    throw err;
  }
  const remote = new Map<string, RemoteFile>();
  const excluded: string[] = [];
  const prefix = t.folder ? `${t.folder}/` : '';
  for (const row of await listFiles(client, t, prefix)) {
    const rel = row.path.slice(prefix.length);
    if (isOutsideSync(rel)) {
      excluded.push(rel);
      continue;
    }
    remote.set(rel, {
      rel,
      version: row.version,
      content_sha256: row.content_sha256,
      content_type: row.content_type || 'application/octet-stream',
      size: row.size ?? 0,
    });
  }
  return { client, remote, excluded };
}

function isNotFound(err: unknown): boolean {
  return err instanceof ApiError && err.status === 404;
}

/** Ask before a change the user must agree to; without a terminal, only `-y` agrees. */
async function agree(question: string, yes: boolean): Promise<boolean> {
  if (yes) return true;
  if (!isInteractive()) {
    throw expected(`${question.trim().replace(/\?$/, '')}: there is no terminal to ask, so pass -y to go ahead.`);
  }
  return confirm(question);
}

function isConflict(err: unknown): boolean {
  return err instanceof ApiError && (err.status === 409 || err.status === 412);
}

const PULL_KEPT: Record<PullKept, string> = {
  edited: 'edited in your files, not pushed yet',
  deleted: 'deleted in your files, not pushed yet (push --prune deletes it from the base)',
};

type PullOutcome = PullConflict | Clash | 'edited_while_pulling';

const PULL_CONFLICT: Record<PullOutcome, string> = {
  both_changed: 'changed in your files and on the base',
  deleted_on_base: 'edited in your files, deleted on the base',
  deleted_locally: 'deleted in your files, changed on the base',
  exists_locally: 'in your files and on the base, with different content',
  case_twin: 'differs only in case from your local',
  file_folder: 'would be both a file and a folder with your local',
  edited_while_pulling: 'changed in your files while pulling — kept; pull again',
};

type PushOutcome = PushConflict | 'changed_while_pushing';

const PUSH_CONFLICT: Record<PushOutcome, string> = {
  changed_on_base: 'changed on the base since your last pull',
  deleted_on_base: 'deleted on the base since your last pull',
  exists_on_base: 'created on the base since your last pull',
  case_twin: "differs only in case from the base's",
  file_folder: 'would make one path both a file and a folder with the base\'s',
  changed_while_pushing: 'changed on the base while pushing — not written',
};

function conflictLine<R extends string>(c: Conflict<R>, texts: Record<R, string>): string {
  return `${safe(c.rel)}: ${texts[c.reason]}${c.other === undefined ? '' : ` "${safe(c.other)}"`}`;
}

/**
 * What settles a set of conflicts. `--overwrite` takes the base's copy of a path, which cannot settle
 * a clash between two paths — a name that differs from another only in case, or one path as both a
 * file and a folder. Only renaming or removing one of the two can.
 */
function remedy(conflicts: Array<Conflict<string>>, overwriteCommand: string): string {
  const isClash = (c: Conflict<string>) => c.reason === 'case_twin' || c.reason === 'file_folder';
  const parts: string[] = [];
  if (conflicts.some((c) => !isClash(c))) {
    parts.push(`take the base's copies with \`${overwriteCommand}\` (commit yours first to merge them)`);
  }
  if (conflicts.some(isClash)) {
    parts.push('rename or remove one path of each pair that differs only in case or is both a file and a folder (--overwrite cannot settle those)');
  }
  return parts.join('; ');
}

export interface PullOptions extends SyncOptions {
  overwrite: boolean;
  yes: boolean;
}

/** `wayai files pull`: the base's files → the local folder, keeping local changes. */
export async function pullFiles(opts: PullOptions): Promise<void> {
  const { target, dir, existing } = resolveSync(opts);
  const { client, remote, excluded } = await readBase(target);
  assertRepresentable([...remote.keys()]);
  const local = scanLocal(dir);
  const plan = planPull({ synced: existing?.files ?? {}, local, remote, overwrite: opts.overwrite });

  if (plan.replacedLocal.length > 0) {
    console.log("These local changes would be replaced with the base's copies:");
    for (const rel of plan.replacedLocal) console.log(`  ${safe(rel)}`);
    if (!(await agree(`Replace ${plan.replacedLocal.length} locally changed file(s)?`, opts.yes))) {
      console.log('Cancelled.');
      return;
    }
  }

  ensureSyncDir(dir);
  const record: SyncRecord = { ...target, files: { ...(existing?.files ?? {}) } };
  for (const rel of plan.forget) delete record.files[rel];
  for (const r of plan.adopt) record.files[r.rel] = syncedFile(r);
  const conflicts: Array<Conflict<PullOutcome>> = [...plan.conflicts];
  const changes: string[] = [];
  const changedWhilePulling: string[] = [];
  try {
    // Removes first: on a case-insensitive filesystem a case-only rename's old name IS its new one.
    // Each leaves the record only once it is gone, so a failed remove keeps its file recorded.
    // A local file edited after the scan is left as it is: the plan never compared that edit.
    for (const rel of plan.removes) {
      if (!matchesScan(dir, rel, local.get(rel))) {
        conflicts.push({ rel, reason: 'edited_while_pulling' });
        continue;
      }
      removeLocal(dir, rel);
      delete record.files[rel];
      changes.push(`${MARK.delete} ${safe(rel)}`);
    }
    for (const w of plan.writes) {
      // A write must land under its own name, with nothing in the way: one the disk holds spelled
      // differently only by case IS that name on a case-insensitive filesystem, and a file where
      // the write needs a folder (or the reverse) cannot take it at all.
      const clash = diskClash(dir, w.rel);
      if (clash !== undefined) {
        conflicts.push({ rel: w.rel, ...clash });
        continue;
      }
      const path = filePath(target, w.rel);
      let bytes: Uint8Array;
      let version = w.remote.version;
      try {
        bytes = (await client.download(path)).bytes;
        if (computeHash(bytes) !== w.remote.content_sha256) {
          // The file changed between the listing and the download. Record the version these bytes
          // ARE, or leave the path for the next pull — never a version the bytes are not.
          const meta = await client.request<FileRow>('GET', `${path}?meta=1`);
          if (meta?.content_sha256 !== computeHash(bytes)) {
            changedWhilePulling.push(w.rel);
            continue;
          }
          version = meta.version;
        }
      } catch (err) {
        // Deleted or moved on the base since the listing: nothing to take, the rest still is.
        if (!isNotFound(err)) throw err;
        changedWhilePulling.push(w.rel);
        continue;
      }
      if (!matchesScan(dir, w.rel, local.get(w.rel))) {
        conflicts.push({ rel: w.rel, reason: 'edited_while_pulling' });
        continue;
      }
      const hash = computeHash(bytes);
      writeLocal(dir, w.rel, bytes);
      record.files[w.rel] = { version, content_sha256: hash };
      changes.push(`${local.has(w.rel) ? MARK.update : MARK.create} ${safe(w.rel)}`);
    }
  } finally {
    writeRecord(dir, record);
  }

  const count = Object.keys(record.files).length;
  console.log(sanitizeTerminalText(`Pulled ${describeSync(target)} → ${dir} (${count} file${count === 1 ? '' : 's'}).`));
  printSection('Changes:', changes);
  printSection('Kept — your local changes:', plan.kept.map((k) => `${safe(k.rel)}: ${PULL_KEPT[k.reason]}`));
  printSection(
    `Conflicts — your copy is kept; to settle them, ${remedy(conflicts, pullCommand(target, opts.dir, true))}:`,
    conflicts.map((c) => conflictLine(c, PULL_CONFLICT)),
  );
  printSection('Changed or deleted on the base while pulling — left as is; pull again to take it:', changedWhilePulling.map(safe));
  printSection(`Not synced — names a synced folder leaves out (${OUTSIDE_SYNC.join(', ')}):`, excluded.map(safe));
}

export interface PushOptions extends SyncOptions {
  dryRun: boolean;
  prune: boolean;
  label?: string;
  yes: boolean;
}

function isTextType(contentType: string): boolean {
  const ct = contentType.split(';')[0].trim().toLowerCase();
  return (
    ct.startsWith('text/') ||
    /^application\/(json|xml|yaml|x-yaml|javascript|x-ndjson)$/.test(ct) ||
    /\+(json|xml)$/.test(ct)
  );
}

/**
 * The changed lines between two texts. The lines both share at the start and the end are set aside
 * first, so a one-line edit to a long document diffs as that line rather than as the whole
 * replacement `lineDiffOf` falls back to past its size bound. An empty line is a line: a removed
 * blank line or trailing newline is listed like any other.
 */
function changedLines(before: string, after: string): string[] {
  const a = sanitizeTerminalText(before).split('\n');
  const b = sanitizeTerminalText(after).split('\n');
  let start = 0;
  while (start < a.length && start < b.length && a[start] === b[start]) start++;
  let end = 0;
  while (end < a.length - start && end < b.length - start && a[a.length - 1 - end] === b[b.length - 1 - end]) end++;
  return lineDiffOf(a.slice(start, a.length - end), b.slice(start, b.length - end));
}

/** The lines push prints for its steps — a text update's changed lines read off the base's copy. */
async function describeSteps(client: Remote, t: Target, dir: string, steps: PushStep[]): Promise<string[]> {
  const lines: string[] = [];
  for (const step of steps) {
    const rel = safe(step.rel);
    if (step.op === 'create') {
      lines.push(`${MARK.create} ${rel} (new, ${step.local.size} bytes)`);
    } else if (step.op === 'delete') {
      lines.push(`${MARK.delete} ${rel} (delete)`);
    } else {
      lines.push(`${MARK.update} ${rel}`);
      if (!isTextType(step.remote.content_type) || step.remote.size > MAX_DIFF_BYTES || step.local.size > MAX_DIFF_BYTES) {
        lines.push(`    (content changed: ${step.remote.size} → ${step.local.size} bytes)`);
        continue;
      }
      let bytes: Uint8Array;
      try {
        bytes = (await client.download(filePath(t, step.rel))).bytes;
      } catch (err) {
        // Deleted on the base since the listing; its `If-Match` will refuse the write.
        if (!isNotFound(err)) throw err;
        lines.push('    (deleted on the base since it was listed)');
        continue;
      }
      const before = Buffer.from(bytes).toString('utf-8');
      const after = readLocal(dir, step.rel).toString('utf-8');
      for (const line of changedLines(before, after)) lines.push(`    ${line}`);
    }
  }
  return lines;
}

function printPushNotes(plan: PushPlan): void {
  printSection('Changed on the base since your last pull — left as is; pull to take it:', plan.changedOnBase.map(safe));
  printSection('Deleted on the base since your last pull — left as is; pull to remove your copy:', plan.deletedOnBase.map(safe));
  printSection('On the base, not in your files — left as is; pull to fetch it:', plan.onlyOnBase.map(safe));
  printSection('Deleted in your files — kept on the base; push with --prune to delete it:', plan.keptOnBase.map(safe));
}

/** `wayai files push`: the local folder's changes → the base, version-checked. */
export async function pushFiles(opts: PushOptions): Promise<void> {
  const label = opts.label === undefined ? undefined : labelValue(opts.label);
  const { target, dir, existing } = resolveSync(opts);
  if (!existing) {
    throw expected(
      `${sanitizeTerminalText(dir)} has no record of a pull (${RECORD_FILE}), so there is nothing to check a push against. ` +
        `Run \`${pullCommand(target, opts.dir)}\` first; a file type with no files pulls an empty record.`,
    );
  }
  const local = scanLocal(dir);
  assertPushable(target.folder, [...local.keys()]);

  const { client, remote } = await readBase(target);
  let plan = planPush({ synced: existing.files, local, remote, prune: opts.prune });
  if (target.folder && plan.steps.some((s) => s.op === 'create')) {
    // A folder's listing cannot see the rest of the file type — `HR/` beside `--folder hr`, say —
    // and a create must not collide with it either. Read only when there is a create to check.
    const prefix = `${target.folder}/`;
    const elsewhere = (await listFiles(client, target, '')).map((r) => r.path).filter((p) => !p.startsWith(prefix));
    plan = planPush({ synced: existing.files, local, remote, prune: opts.prune, folder: target.folder, elsewhere });
  }
  const conflicts: Array<Conflict<PushOutcome>> = [...plan.conflicts];

  console.log(sanitizeTerminalText(`Push ${dir} → ${describeSync(target)}`));
  printSection('Changes:', await describeSteps(client, target, dir, plan.steps));
  printPushNotes(plan);
  printSection('Conflicts — not pushed:', conflicts.map((c) => conflictLine(c, PUSH_CONFLICT)));

  if (opts.dryRun) {
    console.log('\nDry run — nothing pushed.');
    finishWithConflicts(target, opts.dir, conflicts);
    return;
  }

  const record: SyncRecord = { ...existing, files: { ...existing.files } };
  for (const r of plan.adopt) record.files[r.rel] = syncedFile(r);
  for (const rel of plan.forget) delete record.files[rel];
  if (plan.adopt.length > 0 || plan.forget.length > 0) writeRecord(dir, record);

  if (plan.steps.length === 0) {
    if (conflicts.length === 0) console.log(`\nNothing to push — the base already has every change in ${sanitizeTerminalText(dir)}.`);
    finishWithConflicts(target, opts.dir, conflicts);
    return;
  }

  const n = plan.steps.length;
  if (!(await agree(`\nPush ${n} change${n === 1 ? '' : 's'} to ${describeSync(target)}?`, opts.yes))) {
    console.log('Cancelled.');
    return;
  }

  // The record is rewritten after every step that changes it, so a push stopped anywhere leaves
  // it true to what reached the base, and its rerun picks up where it stopped.
  let pushed = 0;
  const refusedDeletes = new Set<string>();
  for (const step of plan.steps) {
    const path = filePath(target, step.rel);
    // A create that clashes with a path this push deletes (a case-only spelling, or a file where it
    // needs a folder) runs only once that delete landed; otherwise the base would hold a pair no
    // pull could write.
    const blockedBy = step.op === 'create' ? step.after?.find((d) => refusedDeletes.has(d.rel)) : undefined;
    if (blockedBy !== undefined) {
      const conflict: Conflict<PushOutcome> = { rel: step.rel, reason: blockedBy.reason, other: blockedBy.rel };
      conflicts.push(conflict);
      console.log(`  ! ${conflictLine(conflict, PUSH_CONFLICT)}`);
      continue;
    }
    try {
      if (step.op === 'delete') {
        await client.request('DELETE', path, undefined, fileWriteHeaders({ ifMatch: step.ifMatch }));
        delete record.files[step.rel];
      } else {
        const bytes = readLocal(dir, step.rel);
        // The bytes were hashed when the folder was scanned; one edited since would be pushed
        // under a plan that never compared it.
        if (computeHash(bytes) !== step.local.content_sha256) {
          throw expected(`${safe(step.rel)} changed while the push ran. Nothing more was pushed; run the push again.`);
        }
        const contentType =
          step.op === 'update' ? step.remote.content_type : guessMimeType(step.rel) ?? 'application/octet-stream';
        const row = await client.upload<FileRow>(
          path,
          bytes,
          contentType,
          fileWriteHeaders({ ifMatch: step.op === 'update' ? step.ifMatch : undefined, label }),
        );
        record.files[step.rel] = {
          version: typeof row?.version === 'number' ? row.version : 0,
          content_sha256: row?.content_sha256 ?? step.local.content_sha256,
        };
      }
      pushed++;
      console.log(`  ${MARK[step.op]} ${safe(step.rel)}`);
    } catch (err) {
      if (step.op === 'delete' && err instanceof ApiError && err.status === 404) {
        // Already gone: nothing to delete, and nothing left to record.
        delete record.files[step.rel];
      } else if (isConflict(err)) {
        // The base refused the write: the file changed after the plan read it.
        if (step.op === 'delete') refusedDeletes.add(step.rel);
        const conflict: Conflict<PushOutcome> = { rel: step.rel, reason: 'changed_while_pushing' };
        conflicts.push(conflict);
        console.log(`  ! ${conflictLine(conflict, PUSH_CONFLICT)}`);
        continue;
      } else {
        throw err;
      }
    }
    writeRecord(dir, record);
  }

  console.log(sanitizeTerminalText(`\nPushed ${pushed} change${pushed === 1 ? '' : 's'} to ${describeSync(target)}.`));
  finishWithConflicts(target, opts.dir, conflicts);
}

/** End a push that left conflicts with a failure that says the base kept those files and what to do. */
function finishWithConflicts(t: Target, dir: string | undefined, conflicts: Array<Conflict<string>>): void {
  const count = conflicts.length;
  if (count === 0) return;
  throw expected(
    `${count} conflict${count === 1 ? '' : 's'} not pushed — the base's copies of those files were left as they are. ` +
      `To settle them, ${remedy(conflicts, pullCommand(t, dir, true))}, then push again.`,
  );
}
